import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { finalize, Subject, take } from 'rxjs';
import { ClientService } from '../../services/client.service';
import { NotificationService } from '../../services/notification.service';
import { SuggestionService } from '../../services/suggestion.service';
import { LoadingComponent } from "../../shared/loading/loading.component";
import { ModalComponent } from '../../shared/modal/modal.component';
import { SearchBarComponent } from "../../shared/searchbar/searchbar.component";
import { SharedTableComponent } from "../../shared/shared-table/shared-table.component";
export interface SuggestionRow {
  term: string;
  client: string;
  enabled: boolean;
  id?: string | number;
}
@Component({
  selector: 'app-suggestion',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatSortModule,
    MatCardModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    SearchBarComponent,
    SharedTableComponent,
    LoadingComponent
],
  templateUrl: './suggestion.component.html',
  styleUrl: './suggestion.component.scss'
})

export class SuggestionComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['suggestion', 'clientName', 'actions'];
  columnLabels: Record<string, string> = {
    suggestion: 'Termine',
    clientName: 'Client',
    enabled: 'Abilitato',
    actions: ''
  };

  isLoading = false;
  hasError = false;
  isLoadingFullScreen = false;

  suggestionsListLoaded$ = new Subject<void>();

  // Stato query
  sortBy = 'suggestion:asc';
  sortField = '';
  sortDirection = '';
  suggestionFilter = {
    search: '' 
  }

  // Paginazione server-side
  pageIndex = 0;
  pageSize = 10;
  totalItems = 0;

  // Opzioni select "client"
  clientSelectOptions = [{ value: '', label: '' }];

  // DataSource tabella
  suggestionsDataSource = new MatTableDataSource<any>();

  constructor(
    private readonly dialog: MatDialog,
    private readonly suggestionService: SuggestionService,
    private readonly clientService: ClientService,
    private readonly notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    // primo load senza filtri
    this.loadSuggestionData(this.pageIndex, this.pageSize, this.sortBy, {});
    // Recupero client per la select
    this.clientService.getClientListAll().subscribe(response => {
      const clientOptions = response as Array<{ value: string; label: string }>;
      this.clientSelectOptions = clientOptions.map((x: any) => ({
        value: x.id,
        label: x.name
      }));
    });
  }

  private currentFilter() {
    const q = this.suggestionFilter.search?.trim() ?? '';
    return q ? { search: q } : {};
  }

  loadSuggestionData(
    pageIndex: number,
    pageSize: number,
    sortBy: string,
    suggestionFilter: any
  ): void {
    this.isLoading = true;
    this.hasError = false;
    this.suggestionService.getSuggestionsList(pageIndex, pageSize, sortBy, suggestionFilter).pipe(finalize(() => { 
      this.isLoading = false;
      this.suggestionsListLoaded$.next();
     })).subscribe({
      next: (response) => {
        const content = response?.content ?? [];
        this.suggestionsDataSource.data = content;
        this.totalItems = response?.totalElements ?? 0;
        this.pageIndex = (response?.number ?? pageIndex) ?? 0;
        this.pageSize  = (response?.size ?? pageSize) ?? this.pageSize;
      },
      error: (err) => {
        console.log('errore nel recupero dei dati', err);
        this.suggestionsDataSource.data = [];
        this.totalItems = 0;
        this.hasError = true;
      },
    });
  }

  // Filtro
  onFilterChanged(value: any) {
    this.suggestionFilter.search = value?.suggestion ?? '';
    this.pageIndex = 0;
    this.loadSuggestionData(this.pageIndex, this.pageSize, 'suggestion:asc', this.currentFilter());
  }

  // Ordinamento
  onSortChanged(sort: Sort) {
    this.sortField = sort.active;
    this.sortDirection = sort.direction || 'asc';
    this.sortBy = `${this.sortField}:${this.sortDirection}`;
    this.pageIndex = 0;
    this.loadSuggestionData(this.pageIndex, this.pageSize, this.sortBy, this.currentFilter());
  }

  // Cambio pagina / page size (server-side)
  onPageChanged(ev: PageEvent) {
    this.pageIndex = ev.pageIndex;
    this.pageSize  = ev.pageSize;
    this.loadSuggestionData(this.pageIndex, this.pageSize, this.sortBy, this.currentFilter());
  }

  openDialog(event: { data: any; type: 'add' | 'edit' | 'delete' }) {
    const dialogRef = this.dialog.open(ModalComponent, {
      width: '400px',
      data: {
        modalType: 'suggestions',
        action: event.type,
        payload: event.data,
        visibleFields: ['clientId', 'suggestion', 'enabled'],
        fieldTypes: {
          clientId: 'select',
          suggestion: 'input',
          enabled: 'checkbox'
        },
        selectOptions: {
          clientId: this.clientSelectOptions
        },
        labels: {
          clientId: 'Client',
          suggestion: 'Termine',
          enabled: 'Abilitato'
        }
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.isLoadingFullScreen = true;

      if (event.type === 'add') {
        console.log('result add', result);
        this.suggestionService.addNewSuggestion(result).pipe(finalize(() => { this.isLoadingFullScreen = false; })).subscribe({
          next: () => {
            this.loadSuggestionData(this.pageIndex, this.pageSize, this.sortBy, this.currentFilter());
            this.suggestionsListLoaded$.pipe(take(1)).subscribe(()=> {
              this.notificationService.entitySuccess('add', 'Suggestion');
            })
          },
          error: (err) => {
            console.log('errore nella richiesta', err);
            this.notificationService.entityError('add', 'Suggestion');
          }
        });

      } else if (event.type === 'edit') {
        console.log('result edit', result);
        const index = this.suggestionsDataSource.data.findIndex((e: any) => e.id === result.id);
        if (index !== -1) {
          this.suggestionService.editSuggestion(result).pipe(finalize(() => { this.isLoadingFullScreen = false; })).subscribe({
            next: () => {
              this.loadSuggestionData(this.pageIndex, this.pageSize, this.sortBy, this.currentFilter());
              this.suggestionsListLoaded$.pipe(take(1)).subscribe(()=> {
                this.notificationService.entitySuccess('update', 'Suggestion');
              })
            },
            error: (err) => {
              console.log('errore nella richiesta', err);
              this.notificationService.entityError('update', 'Suggestion');
            }
          });
        }

      } else if (event.type === 'delete') {
        console.log('result delete', result);
        this.suggestionService.deleteSuggestion(result.id).pipe(finalize(() => { this.isLoadingFullScreen = false; })).subscribe({
          next: () => {
            if (this.pageIndex > 0 && this.suggestionsDataSource.data.length === 1) {
              this.pageIndex = this.pageIndex - 1;
            }
            this.loadSuggestionData(this.pageIndex, this.pageSize, this.sortBy, this.currentFilter());
            this.suggestionsListLoaded$.pipe(take(1)).subscribe(() => {
              this.notificationService.entitySuccess('delete', 'Suggestion');
            })
          },
          error: (err) => {
            console.log('errore nella richiesta', err);
            this.notificationService.entityError('delete', 'Suggestion');
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.suggestionsListLoaded$.complete();
  }
}